/**
 * features/hr/LoanReconciliation.tsx
 * ------------------------------------------------------------------
 * PAYROLL — Loan reconciliation. After each pay run, an auditable view
 * of every balance-tracked deduction (loans, advances, bank schemes,
 * staff fees, residence permits):
 *
 *   1. Loan ledger — per active/cleared loan, the engine-derived
 *      schedule as of a chosen period: original, monthly, collected to
 *      date, outstanding, projected payoff. Uses the SAME engine the
 *      pay run uses (deductionForPeriod), so the numbers match the
 *      slips by construction.
 *   2. Collection check — per staff + deduction type, what the schedule
 *      says should have been collected vs what actually appears on the
 *      saved payslips, with an OK / under / over badge. Surfaces missed
 *      runs or mid-loan edits.
 *
 * Exportable to CSV and a filed PDF for the audit trail. Read-only:
 * this screen changes nothing, it only reports.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Scale, FileSpreadsheet, FileDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { SCHOOL } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  DEDUCTION_TYPES,
  deductionForPeriod,
  deductionMonthlyAmount,
  deductionOpeningBalance,
  deductionStartPeriod,
  deductionCode,
  periodIndex,
  fmtPula,
  round2,
  type DeductionType,
} from '@/lib/hrPayroll';
import { type HrStaff } from '@/lib/hr';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fromIdx = (idx: number): { year: number; month: number } => ({ year: Math.floor(idx / 12), month: (idx % 12) + 1 });
const ymLabel = (p: { year: number; month: number }): string => `${MONTHS[p.month - 1]} ${p.year}`;
const ymShort = (p: { year: number; month: number }): string => `${p.year}-${String(p.month).padStart(2, '0')}`;

interface LedgerRow {
  staffId: string;
  staffName: string;
  type: DeductionType;
  typeLabel: string;
  original: number;
  monthly: number;
  startLabel: string;
  monthsElapsed: number;
  collected: number;
  outstanding: number;
  /** What this loan actually deducts in the reconciliation month. */
  thisMonth: number;
  payoffLabel: string;
  cleared: boolean;
  status: string;
}

interface CheckRow {
  staffId: string;
  staffName: string;
  type: DeductionType;
  typeLabel: string;
  scheduled: number;
  actual: number;
  variance: number;
  state: 'ok' | 'under' | 'over' | 'mixed';
}

export default function LoanReconciliation() {
  const data = useHrData();

  const staffName = (id: string): string => {
    const s: HrStaff | undefined = data.staffById(id);
    return s ? `${s.lastName}, ${s.firstName}` : '—';
  };

  // Balance-tracked types and their payslip line codes.
  const trackedTypes = useMemo(
    () => (Object.values(DEDUCTION_TYPES).filter((c) => c.balanceKey).map((c) => c.type)),
    [],
  );
  const codeToType = useMemo(() => {
    const m: Record<string, DeductionType> = {};
    trackedTypes.forEach((t) => { m[deductionCode(t)] = t; });
    return m;
  }, [trackedTypes]);

  // Pay periods that actually have payslips — the runs you can reconcile to.
  const periodOptions = useMemo(() => {
    const set = new Set<number>();
    data.payslips.forEach((p) => set.add(periodIndex(p.period)));
    const arr = Array.from(set).sort((a, b) => b - a);
    return arr.map((idx) => ({ idx, label: ymLabel(fromIdx(idx)) }));
  }, [data.payslips]);

  const latestIdx = periodOptions[0]?.idx ?? periodIndex({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [reconIdx, setReconIdx] = useState<number>(latestIdx);
  const reconPeriod = fromIdx(reconIdx);

  // Actual deducted on saved payslips, per staff+type, through the recon period.
  const actualByStaffType = useMemo(() => {
    const m = new Map<string, number>();
    for (const slip of data.payslips) {
      if (periodIndex(slip.period) > reconIdx) continue;
      for (const ln of slip.lines) {
        if (ln.kind !== 'deduction') continue;
        const t = codeToType[ln.code];
        if (!t) continue;
        const key = `${slip.staffId}|${t}`;
        m.set(key, round2((m.get(key) ?? 0) + (ln.amount || 0)));
      }
    }
    return m;
  }, [data.payslips, reconIdx, codeToType]);

  // Per-loan engine-derived ledger (active + cleared loans).
  const { ledger, stopped, openEnded, consolidatedAway } = useMemo(() => {
    const ledger: LedgerRow[] = [];
    const stopped: LedgerRow[] = [];
    /* Loans with no balance to reconcile — bank loans, since the school is
       never told what was borrowed. They are listed rather than dropped: a
       deduction that vanishes from the reconciliation screen is a deduction
       nobody checks. There is nothing to reconcile, only an amount to confirm. */
    const openEnded: { staffId: string; staffName: string; typeLabel: string; detail: string; agreed: number; thisMonth: number; }[] = [];
    let consolidatedAway = 0;
    for (const d of data.deductions) {
      const cfg = DEDUCTION_TYPES[d.type];
      if (!cfg.balanceKey) {
        if (cfg.group === 'loan' && d.status === 'active') {
          openEnded.push({
            staffId: d.staffId,
            staffName: staffName(d.staffId),
            typeLabel: cfg.label,
            detail: [d.values?.bankName, d.values?.reference].filter(Boolean).join(' · '),
            agreed: deductionMonthlyAmount(d),
            thisMonth: deductionForPeriod(d, fromIdx(reconIdx)).amount,
          });
        }
        continue;
      }
      const opening = deductionOpeningBalance(d);
      if (opening <= 0) continue;
      const start = deductionStartPeriod(d);
      const startIdx = periodIndex(start);
      const monthly = deductionMonthlyAmount(d); // 0 if stopped
      const base: Omit<LedgerRow, 'monthsElapsed' | 'collected' | 'outstanding' | 'thisMonth' | 'payoffLabel' | 'cleared'> = {
        staffId: d.staffId,
        staffName: staffName(d.staffId),
        type: d.type,
        typeLabel: cfg.label,
        original: opening,
        monthly,
        startLabel: ymShort(start),
        status: d.status,
      };

      if (d.status === 'suspended' || d.status === 'archived') {
        /* A loan folded into a consolidation is NOT outstanding debt. Its
           balance was carried into the surviving loan, which is in the active
           ledger above — listing it here as well would show the same money
           twice and read as a debt nobody is collecting. Counted instead, so
           it does not silently vanish either. */
        if (d.values?.consolidatedInto) { consolidatedAway += 1; continue; }
        stopped.push({ ...base, monthsElapsed: 0, collected: 0, outstanding: opening, thisMonth: 0, payoffLabel: '—', cleared: false });
        continue;
      }

      // Walk the engine period-by-period to the recon month.
      let collected = 0;
      let remaining = opening;
      let cleared = false;
      let payoffIdx: number | null = null;
      const startWalk = Math.min(startIdx, reconIdx);
      for (let i = startWalk; i <= reconIdx; i++) {
        if (i < startIdx) continue;
        const res = deductionForPeriod(d, fromIdx(i));
        collected = round2(collected + res.amount);
        remaining = res.remainingAfter ?? remaining;
        if (res.cleared) { cleared = true; payoffIdx = i; break; }
      }
      collected = round2(Math.min(opening, collected));
      const outstanding = round2(Math.max(0, opening - collected));
      // Projected payoff month if not yet cleared.
      if (payoffIdx === null && monthly > 0) {
        payoffIdx = startIdx + Math.ceil(opening / monthly) - 1;
      }
      const monthsElapsed = reconIdx >= startIdx ? reconIdx - startIdx + 1 : 0;
      /* What comes off in the reconciliation month itself. Not always the
         agreed monthly: an instalment can be changed or paused, and the last
         payment is capped to whatever is left. Shown so a deviation is
         visible without opening the staff member's record. */
      const thisMonth = deductionForPeriod(d, fromIdx(reconIdx)).amount;
      ledger.push({
        ...base,
        monthsElapsed: Math.max(0, monthsElapsed),
        collected,
        outstanding,
        thisMonth,
        payoffLabel: payoffIdx !== null ? ymShort(fromIdx(payoffIdx)) : '—',
        cleared: cleared || outstanding <= 0,
      });
    }
    ledger.sort((a, b) => a.staffName.localeCompare(b.staffName) || a.typeLabel.localeCompare(b.typeLabel));
    stopped.sort((a, b) => a.staffName.localeCompare(b.staffName));
    return { ledger, stopped, openEnded, consolidatedAway };
  }, [data.deductions, reconIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collection check: scheduled (active loans) vs actual-on-slips, per staff+type.
  const check = useMemo(() => {
    // scheduled per staff+type from active ledger rows
    const sched = new Map<string, number>();
    const hasActive = new Set<string>();
    for (const r of ledger) {
      const key = `${r.staffId}|${r.type}`;
      sched.set(key, round2((sched.get(key) ?? 0) + r.collected));
      hasActive.add(key);
    }
    const hasStopped = new Set<string>();
    for (const r of stopped) hasStopped.add(`${r.staffId}|${r.type}`);

    const rows: CheckRow[] = [];
    const keys = new Set<string>([...sched.keys(), ...actualByStaffType.keys()]);
    for (const key of keys) {
      const [staffId, type] = key.split('|') as [string, DeductionType];
      const scheduled = sched.get(key) ?? 0;
      const actual = actualByStaffType.get(key) ?? 0;
      const variance = round2(actual - scheduled);
      let state: CheckRow['state'];
      if (hasStopped.has(key)) {
        state = 'mixed'; // actual includes history from stopped loans of this type
      } else if (Math.abs(variance) <= 0.01) {
        state = 'ok';
      } else if (variance < 0) {
        state = 'under';
      } else {
        state = 'over';
      }
      rows.push({ staffId, staffName: staffName(staffId), type, typeLabel: DEDUCTION_TYPES[type].label, scheduled, actual, variance, state });
    }
    rows.sort((a, b) => {
      const order = { under: 0, over: 1, mixed: 2, ok: 3 } as const;
      return order[a.state] - order[b.state] || a.staffName.localeCompare(b.staffName);
    });
    return rows;
  }, [ledger, stopped, actualByStaffType]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => ({
    loans: ledger.length,
    original: round2(ledger.reduce((a, r) => a + r.original, 0)),
    collected: round2(ledger.reduce((a, r) => a + r.collected, 0)),
    outstanding: round2(ledger.reduce((a, r) => a + r.outstanding, 0)),
    exceptions: check.filter((r) => r.state === 'under' || r.state === 'over').length,
  }), [ledger, check]);

  if (data.loading) return <div className="flex justify-center py-16"><Spinner label="Loading…" /></div>;

  const slug = `loan_reconciliation_${ymShort(reconPeriod)}`;

  function downloadCsv() {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const head = ['Staff', 'Type', 'Original', 'Monthly', 'Started', 'Months', 'Collected to date', 'Outstanding', 'Projected payoff', 'Status'];
    const body = ledger.map((r) => [r.staffName, r.typeLabel, r.original, r.monthly, r.startLabel, r.monthsElapsed, r.collected, r.outstanding, r.payoffLabel, r.cleared ? 'cleared' : r.status].map(esc).join(','));
    const checkHead = ['', '', 'COLLECTION CHECK', '', '', '', '', '', '', ''];
    const ch = ['Staff', 'Type', 'Scheduled', 'Actual on slips', 'Variance', 'Result', '', '', '', ''];
    const cb = check.map((r) => [r.staffName, r.typeLabel, r.scheduled, r.actual, r.variance, r.state, '', '', '', ''].map(esc).join(','));
    const csv = [head.map(esc).join(','), ...body, '', checkHead.map(esc).join(','), ch.map(esc).join(','), ...cb].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${slug}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    const logo = await loadLogoDataUrl().catch(() => null);
    buildLoanReconPdf(ledger, check, { periodLabel: ymLabel(reconPeriod), date: new Date().toISOString().slice(0, 10), totals }, logo).save(`${slug}.pdf`);
  }

  return (
    <div className="space-y-6">
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-end gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Reconcile through</span>
              <select
                value={reconIdx}
                onChange={(e) => setReconIdx(Number(e.target.value))}
                className={glassFieldClass('py-2 text-sm w-44')}
              >
                {periodOptions.length === 0 && <option value={reconIdx}>{ymLabel(reconPeriod)}</option>}
                {periodOptions.map((p) => <option key={p.idx} value={p.idx}>{p.label}</option>)}
              </select>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 pb-2 max-w-md">
              Balances are derived by the same engine the pay run uses, then checked against the
              deductions that actually appear on saved payslips.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsv} disabled={ledger.length === 0} className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
              <FileSpreadsheet size={15} /> CSV
            </button>
            <button onClick={downloadPdf} disabled={ledger.length === 0} className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
              <FileDown size={15} /> PDF
            </button>
          </div>
        </div>
      </SoftCard>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatChip tone="slate" value={String(totals.loans)} label="Active loans" icon={Scale} />
        <StatChip tone="blue" value={fmtPula(totals.original)} label="Total original" />
        <StatChip tone="gold" value={fmtPula(totals.collected)} label="Collected to date" />
        <StatChip tone="red" value={fmtPula(totals.outstanding)} label="Outstanding" />
        <StatChip tone={totals.exceptions > 0 ? 'red' : 'slate'} value={String(totals.exceptions)} label="Collection flags" icon={totals.exceptions > 0 ? AlertTriangle : CheckCircle2} />
      </div>

      {/* Collection check — exceptions first */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Collection check — schedule vs payslips</h3>
        </div>
        <div className="p-5 overflow-x-auto">
          {check.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No balance-tracked deductions to check.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Staff</th>
                <th className="py-2 px-3 font-medium">Type</th>
                <th className="py-2 px-3 font-medium text-right">Scheduled</th>
                <th className="py-2 px-3 font-medium text-right">On payslips</th>
                <th className="py-2 px-3 font-medium text-right">Variance</th>
                <th className="py-2 pl-3 font-medium">Result</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {check.map((r, i) => (
                  <tr key={`${r.staffId}-${r.type}-${i}`}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{r.staffName}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.typeLabel}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.scheduled)}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.actual)}</td>
                    <td className={`py-2 px-3 text-right font-medium ${r.variance === 0 ? 'text-gray-500' : r.variance < 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>{r.variance === 0 ? '—' : fmtPula(r.variance)}</td>
                    <td className="py-2 pl-3"><CheckBadge state={r.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {consolidatedAway > 0 && (
            <p className="text-[11px] text-gray-400 mt-3">
              {consolidatedAway} loan{consolidatedAway === 1 ? ' was' : 's were'} folded into a consolidated loan and
              {consolidatedAway === 1 ? ' is' : ' are'} not listed separately — that balance is carried by the
              surviving loan above, and showing it twice would read as debt nobody is collecting.
            </p>
          )}
        </div>
      </SoftCard>

      {/* Loan ledger */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Loan ledger — balances as of {ymLabel(reconPeriod)}</h3>
        </div>
        <div className="p-5 overflow-x-auto">
          {ledger.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No active balance-tracked loans.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Staff</th>
                <th className="py-2 px-3 font-medium">Type</th>
                <th className="py-2 px-3 font-medium text-right">Original</th>
                <th className="py-2 px-3 font-medium text-right">Monthly</th>
                <th className="py-2 px-3 font-medium text-right">This month</th>
                <th className="py-2 px-3 font-medium text-right">Collected</th>
                <th className="py-2 px-3 font-medium text-right">Outstanding</th>
                <th className="py-2 pl-3 font-medium text-right">Payoff</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {ledger.map((r, i) => (
                  <tr key={`${r.staffId}-${r.type}-${i}`}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{r.staffName}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.typeLabel}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.original)}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.monthly)}</td>
                    <td className={`py-2 px-3 text-right tabular-nums ${
                      round2(r.thisMonth) !== round2(r.monthly)
                        ? 'font-bold text-amber-700 dark:text-amber-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`} title={round2(r.thisMonth) !== round2(r.monthly) ? `Differs from the agreed ${fmtPula(r.monthly)}` : undefined}>
                      {fmtPula(r.thisMonth)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{fmtPula(r.collected)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-gray-800 dark:text-gray-100">{r.cleared ? <span className="text-emerald-600 dark:text-emerald-400">Cleared</span> : fmtPula(r.outstanding)}</td>
                    <td className="py-2 pl-3 text-right text-gray-500 dark:text-gray-400">{r.payoffLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </SoftCard>

      {openEnded.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Bank loans — nothing to reconcile, only an amount to confirm</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              The school is not told what was borrowed or over how many months, so there is no balance to run down.
              These recur until the bank says otherwise.
            </p>
          </div>
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Staff</th>
                <th className="py-2 px-3 font-medium">Bank</th>
                <th className="py-2 px-3 font-medium text-right">Agreed</th>
                <th className="py-2 pl-3 font-medium text-right">This month</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {openEnded.map((r, i) => (
                  <tr key={`${r.staffId}-oe-${i}`}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{r.staffName}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.detail || r.typeLabel}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.agreed)}</td>
                    <td className={`py-2 pl-3 text-right tabular-nums ${
                      round2(r.thisMonth) !== round2(r.agreed)
                        ? 'font-bold text-amber-700 dark:text-amber-300'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}>
                      {fmtPula(r.thisMonth)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                <td className="py-2 pr-3">{openEnded.length} loans</td>
                <td />
                <td className="py-2 px-3 text-right">{fmtPula(openEnded.reduce((a, r) => a + r.agreed, 0))}</td>
                <td className="py-2 pl-3 text-right">{fmtPula(openEnded.reduce((a, r) => a + r.thisMonth, 0))}</td>
              </tr></tfoot>
            </table>
          </div>
        </SoftCard>
      )}

      {stopped.length > 0 && (
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Stopped (suspended / archived) — collection halted</h3>
          </div>
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Staff</th>
                <th className="py-2 px-3 font-medium">Type</th>
                <th className="py-2 px-3 font-medium text-right">Original</th>
                <th className="py-2 pl-3 font-medium">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {stopped.map((r, i) => (
                  <tr key={`${r.staffId}-${r.type}-${i}`}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{r.staffName}</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.typeLabel}</td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.original)}</td>
                    <td className="py-2 pl-3 text-gray-500 dark:text-gray-400 capitalize">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}
    </div>
  );
}

function CheckBadge({ state }: { state: CheckRow['state'] }) {
  const map = {
    ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    under: { label: 'Under-collected', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
    over: { label: 'Over-collected', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    mixed: { label: 'Mixed — review', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  } as const;
  const b = map[state];
  return <span className={`px-2 py-0.5 rounded-full text-xs ${b.cls}`}>{b.label}</span>;
}

/* ------------------------------------------------------------------ */
function buildLoanReconPdf(
  ledger: LedgerRow[],
  check: CheckRow[],
  meta: { periodLabel: string; date: string; totals: { loans: number; original: number; collected: number; outstanding: number; exceptions: number } },
  logo: string | null,
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 14; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  const header = (title: string) => {
    if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 16, 16); } catch { /* ignore */ } }
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
    pdf.text(SCHOOL.name, MX + 20, y + 4);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
    pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}`, MX + 20, y + 9);
    y += 20;
    setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 7;
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
    pdf.text(title, MX, y);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
    pdf.text(`As of ${meta.periodLabel}  ·  generated ${meta.date}`, right, y, { align: 'right' });
    y += 7;
  };

  // Ledger
  const cName = MX + 2, cType = right - 118, cOrig = right - 86, cMon = right - 62, cCol = right - 38, cOut = right - 2;
  const ledgerHead = () => {
    setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text('STAFF', cName, y);
    pdf.text('Type', cType, y);
    pdf.text('Original', cOrig, y, { align: 'right' });
    pdf.text('Monthly', cMon, y, { align: 'right' });
    pdf.text('Collected', cCol, y, { align: 'right' });
    pdf.text('Outstanding', cOut, y, { align: 'right' });
    y += 6;
  };

  header('LOAN RECONCILIATION');
  ledgerHead();
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  for (const r of ledger) {
    if (y > 270) { pdf.addPage(); y = 16; header('LOAN RECONCILIATION (cont.)'); ledgerHead(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); }
    setT(INK);
    pdf.text((pdf.splitTextToSize(r.staffName, cType - cName - 3) as string[])[0] ?? r.staffName, cName, y);
    setT(MUTED); pdf.text((pdf.splitTextToSize(r.typeLabel, cOrig - cType - 14) as string[])[0] ?? r.typeLabel, cType, y);
    setT(INK); pdf.text(fmtPula(r.original), cOrig, y, { align: 'right' });
    setT(MUTED); pdf.text(fmtPula(r.monthly), cMon, y, { align: 'right' });
    setT(INK); pdf.text(fmtPula(r.collected), cCol, y, { align: 'right' });
    pdf.setFont('helvetica', 'bold');
    if (r.cleared) { setT([5, 150, 105]); pdf.text('Cleared', cOut, y, { align: 'right' }); }
    else { setT(INK); pdf.text(fmtPula(r.outstanding), cOut, y, { align: 'right' }); }
    pdf.setFont('helvetica', 'normal');
    y += 5.4;
  }
  y += 1; setD(RULE); pdf.line(MX, y, right, y); y += 6;
  setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text(`${meta.totals.loans} loan${meta.totals.loans === 1 ? '' : 's'}`, cName, y);
  pdf.text(fmtPula(meta.totals.collected), cCol, y, { align: 'right' });
  setT(NAVY); pdf.text(fmtPula(meta.totals.outstanding), cOut, y, { align: 'right' });
  y += 12;

  // Collection check
  const exceptions = check.filter((r) => r.state !== 'ok');
  if (exceptions.length > 0) {
    if (y > 250) { pdf.addPage(); y = 16; }
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
    pdf.text('COLLECTION CHECK — items to review', MX, y); y += 7;
    const kName = MX + 2, kType = right - 96, kSch = right - 64, kAct = right - 34, kRes = right - 2;
    setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text('STAFF', kName, y); pdf.text('Type', kType, y);
    pdf.text('Scheduled', kSch, y, { align: 'right' });
    pdf.text('On slips', kAct, y, { align: 'right' });
    pdf.text('Result', kRes, y, { align: 'right' });
    y += 6;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
    for (const r of exceptions) {
      if (y > 280) { pdf.addPage(); y = 16; }
      setT(INK); pdf.text((pdf.splitTextToSize(r.staffName, kType - kName - 3) as string[])[0] ?? r.staffName, kName, y);
      setT(MUTED); pdf.text(r.typeLabel, kType, y);
      setT(INK); pdf.text(fmtPula(r.scheduled), kSch, y, { align: 'right' });
      pdf.text(fmtPula(r.actual), kAct, y, { align: 'right' });
      const label = r.state === 'under' ? 'Under' : r.state === 'over' ? 'Over' : 'Review';
      if (r.state === 'under') setT([185, 28, 28]); else if (r.state === 'over') setT([180, 83, 9]); else setT(MUTED);
      pdf.setFont('helvetica', 'bold'); pdf.text(label, kRes, y, { align: 'right' }); pdf.setFont('helvetica', 'normal');
      y += 5.4;
    }
  } else {
    setT([5, 150, 105]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
    pdf.text('Collection check: all balance-tracked deductions reconcile to the payslips.', MX, y);
  }

  return pdf;
}
